import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Sidenav from "../../vendor/libs/sidenav";
import layoutHelpers from "./helpers";
import { hasAnyRole, hasRole } from "../../utils";
import { AirportGreeterRole, canAccessReviewsFeature, getAirportGreeterRoute, getAllowedRegionConfigsForRole, getDomainInfo, PricingManagerRole } from "../../utils/userAccess";
import { withTranslation } from "react-i18next";
import { getDashboardConfiguration } from "../../api/ExternalApi";

class LayoutSidenav extends Component {
  constructor(props) {
    super(props);
    this.state = { enableChat: false, enablePhoneSupport: false, scoringEnabled: false, enableQuotations: false };
    this._chatConfigWebsiteGuid = null;
  }

  // Mirrors the roles /api/bookings/GetDashboardConfiguration actually accepts. Support Operator belongs
  // here: without the config fetch its flags stay false, which silently hid the whole Support menu from
  // the very role that lives in it.
  canLoadDashboardConfiguration(props) {
    const selectedWebsite = getDomainInfo(props).selectedWebsite;
    return hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher", "Support Operator"]);
  }

  componentDidMount() {
    this._fetchChatConfig(this.props);
  }

  componentDidUpdate(prevProps) {
    const prevWebsite = getDomainInfo(prevProps).selectedWebsite;
    const nextWebsite = getDomainInfo(this.props).selectedWebsite;
    if (prevWebsite?.websiteGuid !== nextWebsite?.websiteGuid) {
      this._fetchChatConfig(this.props);
    }
  }

  // One call drives several sidebar flags (chat support + driver/supplier scoring) so we don't fire
  // separate per-feature config requests from the sidebar.
  _fetchChatConfig(props) {
    const domainInfo = getDomainInfo(props);
    const websiteGuid = domainInfo.selectedWebsite?.websiteGuid;
    if (!this.canLoadDashboardConfiguration(props)) {
      this._chatConfigWebsiteGuid = null;
      if (this.state.enableChat || this.state.enablePhoneSupport || this.state.scoringEnabled || this.state.enableQuotations) {
        this.setState({ enableChat: false, enablePhoneSupport: false, scoringEnabled: false, enableQuotations: false });
      }
      return;
    }
    if (!websiteGuid || websiteGuid === this._chatConfigWebsiteGuid) return;
    this._chatConfigWebsiteGuid = websiteGuid;
    getDashboardConfiguration(websiteGuid)
      .then((config) => {
        this.setState({
          enableChat: config.enableChat,
          enablePhoneSupport: config.enablePhoneSupport,
          scoringEnabled: config.enableDriverSupplierScoring,
          enableQuotations: config.enableQuotations,
        });
      })
      .catch(() => { /* keep false on error */ });
  }

  translate(key) {
    return this.props.t(key);
  }

  layoutSidenavClasses() {
    let bg = this.props.sidenavBg;

    if (this.props.orientation === "horizontal" && (bg.indexOf(" sidenav-dark") !== -1 || bg.indexOf(" sidenav-light") !== -1)) {
      bg = bg.replace(" sidenav-dark", "").replace(" sidenav-light", "").replace("-darker", "").replace("-dark", "");
    }

    return `bg-${bg} ` + (this.props.orientation !== "horizontal" ? "layout-sidenav" : "layout-sidenav-horizontal container-p-x flex-grow-0");
  }

  toggleSidenav(e) {
    e.preventDefault();
    layoutHelpers.toggleCollapsed();
  }

  isMenuActive(singleOrMultipleUrls) {
    if (Array.isArray(singleOrMultipleUrls)) {
      const foundIndex = singleOrMultipleUrls.findIndex((el) => this.props.location.pathname.indexOf(el) === 0);
      return foundIndex >= 0;
    } else {
      return this.props.location.pathname.indexOf(singleOrMultipleUrls) === 0;
    }
  }

  isMenuOpen(singleOrMultipleUrls) {
    if (this.props.orientation === "horizontal") return false;

    if (Array.isArray(singleOrMultipleUrls)) {
      const foundIndex = singleOrMultipleUrls.findIndex((el) => this.props.location.pathname.indexOf(el) === 0);
      return foundIndex >= 0;
    } else {
      return this.props.location.pathname.indexOf(singleOrMultipleUrls) === 0 && this.props.orientation !== "horizontal";
    }
  }

  render() {
    const domainInfo = getDomainInfo(this.props);
    const selectedWebsite = domainInfo.selectedWebsite;
    const defaultWebsiteName = domainInfo.defaultWebsiteName;
    const isGoldsainte = domainInfo.isGoldsainte;
    const isTravelThru = domainInfo.isTravelThru;
    const isTravelThruWebsite = selectedWebsite != null && selectedWebsite.websiteGuid === "92e017f9-3c31-4045-bb60-d1d0f3615e33";
    const canShowReviews = canAccessReviewsFeature(this.props);
    const isCarOwner = hasRole(domainInfo.selectedWebsite, "CarOwner");
    const isClient = hasRole(domainInfo.selectedWebsite, "Client");
    const isDriver = hasRole(domainInfo.selectedWebsite, "Driver");
    const isAirportGreeter = hasRole(domainInfo.selectedWebsite, AirportGreeterRole);
    const isFleetManager = hasRole(domainInfo.selectedWebsite, "Fleet Manager");
    const airportGreeterRegions = getAllowedRegionConfigsForRole(selectedWebsite, AirportGreeterRole);
    const airportGreeterDefaultRoute = getAirportGreeterRoute(selectedWebsite);

    const enableExterimentalFeatures = !domainInfo.isProduction;

    return (
      <Sidenav
        orientation={this.props.orientation}
        className={`${this.layoutSidenavClasses()} fixed-sidenav small-scale`}
        style={{ boxShadow: "0px 2px 16px 0px #1a1a1a3d" }}
      >
        {/* Brand demo (see src/demo.css) */}
        {this.props.orientation !== "horizontal" && (
          <React.Fragment>
            <div className="app-brand demo">
              <img
                alt="chauffeur booking software"
                className="app-brand-logo demo"
                style={{ backgroundColor: "white" }}
                src="app-icon.png"
                height={30}
                width={30}
              ></img>
              <Link to="/" className="app-brand-text demo sidenav-text font-weight-normal ml-2">
                {defaultWebsiteName}
              </Link>
            </div>
            <Sidenav.Divider className="mt-0" />
          </React.Fragment>
        )}
        {/* Inner */}
        <div className={`sidenav-inner ${this.props.orientation !== "horizontal" ? "py-1" : ""}`}>
          {isCarOwner && (
            <>
              <Sidenav.RouterLink to="/rentals" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.rentals.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/vehicles" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.myVehicles.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/payments" exact={true} icon="ion ion-ios-wallet">
                {this.props.t("common:sidenav.payments.menu")}
              </Sidenav.RouterLink>
            </>
          )}

          {/* Client menu items */}
          {isClient && (
            <>
              <Sidenav.RouterLink to="/book" exact={true} icon="ion ion-ios-add">
                {this.props.t("common:sidenav.newBooking.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/bookings" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.bookings.menu")}
              </Sidenav.RouterLink>
            </>
          )}

          {/* Driver menu items */}
          {isDriver && (
            <>
              <Sidenav.RouterLink to="/driver-bookings" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.driverBookings.menu")}
              </Sidenav.RouterLink>

              {enableExterimentalFeatures && (
                <Sidenav.RouterLink to="/suggestion-price-driver" exact={true} icon="ion ion ion-md-build">
                  {this.props.t("common:sidenav.suggestion.menu")}
                </Sidenav.RouterLink>
              )}
            </>
          )}

          {(isClient || isDriver || isAirportGreeter || isFleetManager) && (
            <Sidenav.RouterLink to="/my-profile" exact={true} icon="ion ion-ios-person">
              {this.props.t("common:sidenav.profile.menu")}
            </Sidenav.RouterLink>
          )}

          {isAirportGreeter && airportGreeterRegions.length > 0 && (
            <Sidenav.Menu
              icon="ion ion-ios-airplane"
              linkText="Airport Pickups"
              active={this.isMenuActive(["/greeter/airport-pickups"])}
              open={this.isMenuOpen(["/greeter/airport-pickups"])}
            >
              {airportGreeterRegions.map((region) => (
                <Sidenav.RouterLink
                  key={region.pricePlanGuid}
                  to={`/greeter/airport-pickups/${selectedWebsite.websiteGuid}?regionGuid=${encodeURIComponent(region.pricePlanGuid)}&regionName=${encodeURIComponent(region.regionName || "Region")}`}
                  exact={true}
                  icon="ion ion-ios-pin"
                >
                  {region.regionName || "Region"}
                </Sidenav.RouterLink>
              ))}
            </Sidenav.Menu>
          )}

          {isAirportGreeter && airportGreeterRegions.length === 0 && airportGreeterDefaultRoute && (
            <Sidenav.RouterLink to={airportGreeterDefaultRoute} icon="ion ion-ios-airplane">
              Airport Pickups
            </Sidenav.RouterLink>
          )}

          {hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) &&
            (isTravelThru && hasRole(selectedWebsite, "Administrator") ? (
              <Sidenav.Menu
                icon="ion ion-ios-document"
                linkText={this.props.t("common:sidenav.createBooking.menu")}
                active={this.isMenuActive(["/booking", "/import-bookings"])}
                open={this.isMenuOpen(["/booking", "/import-bookings"])}
              >
                <Sidenav.RouterLink to="/booking" exact={true} icon="ion ion-ios-add">
                  {this.props.t("common:sidenav.newBooking.menu")}
                </Sidenav.RouterLink>

                <Sidenav.RouterLink to="/import-bookings" exact={true} icon="ion ion-ios-download">
                  {this.props.t("common:sidenav.importBooking.menu")}
                </Sidenav.RouterLink>
              </Sidenav.Menu>
            ) : (
              <Sidenav.RouterLink to="/booking" exact={true} icon="ion ion-ios-add">
                {this.props.t("common:sidenav.newBooking.menu")}
              </Sidenav.RouterLink>
            ))}

          {hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) &&
            (isTravelThru && hasRole(selectedWebsite, "Administrator") ? (
              <Sidenav.Menu
                icon="ion ion-ios-list"
                linkText={this.props.t("common:sidenav.dispatch.menu")}
                active={this.isMenuActive(["/overview", "/dispatch"])}
                open={this.isMenuOpen(["/overview", "/dispatch"])}
              >
                <Sidenav.RouterLink to="/overview" exact={true} icon="ion ion-ios-stats">
                  {this.props.t("common:sidenav.dispatch.overview.menu")}
                </Sidenav.RouterLink>

                <Sidenav.RouterLink to="/dispatch" exact={true} icon="ion ion-ios-clipboard">
                  {this.props.t("common:sidenav.dispatch.menu")}
                </Sidenav.RouterLink>
              </Sidenav.Menu>
            ) : (
              <Sidenav.RouterLink to="/dispatch" exact={true} icon="ion ion-ios-list">
                {this.props.t("common:sidenav.dispatch.menu")}
              </Sidenav.RouterLink>
            ))}

          {hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) && (
            <Sidenav.RouterLink to="/finished-bookings" exact={true} icon="ion ion-ios-checkmark-circle">
              {this.props.t("common:sidenav.finishedBookings.menu")}
            </Sidenav.RouterLink>
          )}

          {isGoldsainte && hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) && (
            <Sidenav.RouterLink to="/rentals" exact={true} icon="ion ion-ios-car">
              {this.props.t("common:sidenav.rentals.menu")}
            </Sidenav.RouterLink>
          )}

          {hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) && (
            <Sidenav.RouterLink to="/live-map" exact={true} icon="ion ion-ios-map">
              {this.props.t("common:sidenav.liveMap.menu")}
            </Sidenav.RouterLink>
          )}

          {this.state.enableQuotations && hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) && (
            <Sidenav.Menu
              icon="ion ion-ios-paper"
              linkText={this.props.t("common:sidenav.quotations.menu") || "Quotations"}
              active={this.isMenuActive(["/quotations"])}
              open={this.isMenuOpen(["/quotations"])}
            >
              <Sidenav.RouterLink to="/quotations/new" exact={true} icon="ion ion-ios-add">
                {this.props.t("common:sidenav.quotations.new") || "New Quotation"}
              </Sidenav.RouterLink>

              <Sidenav.RouterLink to="/quotations" exact={true} icon="ion ion-ios-list">
                {this.props.t("common:sidenav.quotations.list") || "Quotations"}
              </Sidenav.RouterLink>

              <Sidenav.RouterLink to="/quotations/results" exact={true} icon="ion ion-ios-stats">
                {this.props.t("common:sidenav.quotations.results") || "Results"}
              </Sidenav.RouterLink>

              {/* The network pool holds every website's bidders: super admins only (supplier_network.md, decision 7). */}
              {this.props.isSuperAdmin && (
                <Sidenav.RouterLink to="/quotations/network" exact={true} icon="ion ion-ios-people">
                  {this.props.t("common:sidenav.quotations.network")}
                </Sidenav.RouterLink>
              )}
            </Sidenav.Menu>
          )}

          {hasAnyRole(selectedWebsite, ["Administrator", "Fleet Manager"]) && (
            <Sidenav.Menu
              icon="ion ion-ios-git-network"
              linkText={this.props.t("common:sidenav.partners.menu")}
              active={this.isMenuActive(["/suppliers", "/drivers"])}
              open={this.isMenuOpen(["/suppliers", "/drivers"])}
            >
              <Sidenav.RouterLink to="/drivers" exact={true} icon="ion ion-ios-person">
                {this.props.t("common:sidenav.driver.menu")}
              </Sidenav.RouterLink>

              <Sidenav.RouterLink to="/suppliers" exact={true} icon="ion ion-ios-business">
                {this.props.t("common:sidenav.suppliers.menu")}
              </Sidenav.RouterLink>

              {hasRole(selectedWebsite, "Administrator") && (
                <Sidenav.RouterLink to="/pickup-instructions" exact={true} icon="ion ion-ios-compass">
                  {this.props.t("common:sidenav.pickupInstructions.menu")}
                </Sidenav.RouterLink>
              )}

              {isTravelThru && hasRole(selectedWebsite, "Administrator") && (
                <Sidenav.RouterLink to="/auto-assign-rules" exact={true} icon="ion ion-ios-options">
                  {this.props.t("common:sidenav.autoAssignRules.menu")}
                </Sidenav.RouterLink>
              )}
            </Sidenav.Menu>
          )}

          {hasRole(selectedWebsite, "Administrator") && (
            <Sidenav.RouterLink to="/clients" exact={true} icon="ion ion-ios-people">
              {this.props.t("common:sidenav.clients.menu")}
            </Sidenav.RouterLink>
          )}

          {hasRole(selectedWebsite, "Administrator") && (
            <Sidenav.RouterLink to="/coupons" exact={true} icon="ion ion-ios-cash">
              {this.props.t("common:sidenav.coupons.menu")}
            </Sidenav.RouterLink>
          )}

          {hasRole(selectedWebsite, "Administrator") && (
            <Sidenav.RouterLink to="/customer" exact={true} icon="ion ion-ios-person">
              {this.props.t("common:sidenav.customer.menu")}
            </Sidenav.RouterLink>
          )}

          {hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher"]) && isGoldsainte && (
            <Sidenav.Menu
              icon="ion ion-ios-clipboard"
              linkText={this.props.t("common:sidenav.goldsainte.menu")}
              active={this.isMenuActive(["/carowners", "/vehicles", "/vehicles-types"])}
              open={this.isMenuOpen(["/carowners", "/vehicles", "/vehicles-types"])}
            >
              <Sidenav.RouterLink to="/carowners" exact={true} icon="ion ion-ios-people">
                {this.props.t("common:sidenav.carOwners.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/vehicles" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.carShare.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/vehicles-types" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.vehiclesTypes.menu")}
              </Sidenav.RouterLink>
            </Sidenav.Menu>
          )}

          {hasRole(selectedWebsite, "Administrator") && (
            <Sidenav.Menu
              icon="ion ion-ios-clipboard"
              linkText={this.props.t("common:sidenav.reports.menu")}
              active={this.isMenuActive(["/reports", "/rankings", "/payouts-outstanding", "/payments", "/reports-booking-insights", canShowReviews && !isTravelThruWebsite ? "/reviews" : null])}
              open={this.isMenuOpen(["/reports", "/rankings", "/payouts-outstanding", "/payments", "/reports-booking-insights", canShowReviews && !isTravelThruWebsite ? "/reviews" : null])}
            >
              {hasRole(selectedWebsite, "Administrator") && (
                <Sidenav.RouterLink to="/reports-bookings" exact={true} icon="ion ion-ios-stats">
                  {this.props.t("common:sidenav.reports.bookings.menu")}
                </Sidenav.RouterLink>
              )}
              {hasRole(selectedWebsite, "Administrator") && this.state.scoringEnabled && (
                <Sidenav.RouterLink to="/rankings" exact={true} icon="ion ion-ios-trending-up">
                  Rankings
                </Sidenav.RouterLink>
              )}
              {/* Internal for now: reached by URL, deliberately not listed. The route and its
                  Administrator gate stay in place - uncomment when it is ready to be found. */}
              {/* {hasRole(selectedWebsite, "Administrator") && (
                <Sidenav.RouterLink to="/reports-booking-insights" exact={true} icon="ion ion-ios-pulse">
                  {this.props.t("common:sidenav.reports.bookingInsights.menu")}
                </Sidenav.RouterLink>
              )} */}
              {hasRole(selectedWebsite, "Administrator") && (
                <Sidenav.RouterLink to="/payments" exact={true} icon="ion ion-ios-wallet">
                  {this.props.t("common:sidenav.reports.payments.menu")}
                </Sidenav.RouterLink>
              )}
              {hasRole(selectedWebsite, "Administrator") && canShowReviews && !isTravelThruWebsite && (
                <Sidenav.RouterLink to="/reviews" exact={true} icon="ion ion-ios-star">
                  {this.props.t("common:sidenav.reports.reviews.menu")}
                </Sidenav.RouterLink>
              )}
              {hasRole(selectedWebsite, "Administrator") && !isTravelThru && this.props.isBookingDotCom && (
                <Sidenav.RouterLink to="/operations" exact={true} icon="ion ion-ios-stats">
                  {this.props.t("common:sidenav.reports.operations.menu")}
                </Sidenav.RouterLink>
              )}
            </Sidenav.Menu>
          )}

          {hasRole(selectedWebsite, "Administrator") && this.props.isBookingDotCom && isTravelThru && (
            <Sidenav.Menu
              icon="ion ion-md-speedometer"
              linkText={this.props.t("common:sidenav.bookingCom.menu")}
              active={this.isMenuActive([
                "/bookingdotcom",
                "/bookingdotcom-requests",
                "/bookingdotcom-stats",
                "/price-assistant",
                "/price-missing",
                "/region-missing",
                "/price-requests",
                "/request-conversion",
                "/price-setup",
              ])}
              open={this.isMenuOpen([
                "/bookingdotcom",
                "/bookingdotcom-requests",
                "/bookingdotcom-stats",
                "/price-assistant",
                "/price-missing",
                "/region-missing",
                "/price-requests",
                "/request-conversion",
                "/price-setup",
              ])}
            >
              <Sidenav.RouterLink to="/bookingdotcom-requests" exact={true} icon="ion ion-ios-list">
                {this.props.t("common:sidenav.bookingCom.requests.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/bookingdotcom-stats" exact={true} icon="ion ion-ios-stats">
                {this.props.t("common:sidenav.bookingCom.statistics.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/price-requests" exact={true} icon="ion ion-logo-usd">
                {this.props.t("common:sidenav.bookingCom.priceRequests.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/request-conversion" exact={true} icon="ion ion-ios-analytics">
                Request Conversion
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/price-assistant" exact={true} icon="ion ion-logo-usd">
                {this.props.t("common:sidenav.bookingCom.pricingAssistant.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/price-missing" exact={true} icon="ion ion-ios-laptop">
                {this.props.t("common:sidenav.bookingCom.priceMissing.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/region-missing" exact={true} icon="ion ion-ios-map">
                {this.props.t("common:sidenav.bookingCom.regionMissing.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/price-setup" exact={true} icon="ion ion-ios-cog">
                {this.props.t("common:sidenav.bookingCom.priceSetup.menu")}
              </Sidenav.RouterLink>
            </Sidenav.Menu>
          )}

          {hasRole(selectedWebsite, "Administrator") && (
            <Sidenav.Menu
              icon="ion ion-logo-usd"
              linkText={this.props.t("common:sidenav.pricing.menu")}
              active={this.isMenuActive(["/edit-prices", "/prices", "/price-editor", "/services", "/nested-regions", "/partner-setup", "/partner-prices", "/pricing-requests", "/pricing"])}
              open={this.isMenuOpen(["/edit-prices", "/prices", "/price-editor", "/services", "/nested-regions", "/partner-setup", "/partner-prices", "/pricing-requests", "/pricing"])}
            >
              {(isTravelThru || selectedWebsite.hasV2Pricing) && (
                <>
                  <Sidenav.RouterLink to={`/edit-prices?website=${selectedWebsite.websiteGuid}`} exact={true} icon="ion ion-ios-settings">
                    {this.props.t("common:sidenav.pricing.editPrices.menu")}
                  </Sidenav.RouterLink>
                </>
              )}
              <Sidenav.RouterLink to={`/prices`} exact={true} icon="ion ion-ios-settings">
                {this.props.t("common:sidenav.pricing.prices.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/price-editor" exact={true} icon="ion ion-ios-grid">
                Price Editor
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/provider-pricing" exact={true} icon="ion ion-ios-pricetags">
                Provider Pricing
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to="/services" exact={true} icon="ion ion-ios-car">
                {this.props.t("common:sidenav.pricing.services.menu")}
              </Sidenav.RouterLink>

              {enableExterimentalFeatures && (
                <>
                  <Sidenav.RouterLink to="/pricing-requests" exact={true} icon="ion ion-ios-pulse">
                    Requests
                  </Sidenav.RouterLink>
                  <Sidenav.RouterLink to={`/nested-regions`} exact={true} icon="ion ion-ios-globe">
                    {this.props.t("common:sidenav.pricing.nestedRegions.menu")}
                  </Sidenav.RouterLink>

                  <Sidenav.RouterLink to={`/partner-setup`} exact={true} icon="ion ion-md-settings">
                    {this.props.t("common:sidenav.pricing.partnerSetup.menu")}
                  </Sidenav.RouterLink>

                  <Sidenav.RouterLink to={`/partner-prices`} exact={true} icon="ion ion-logo-usd">
                    {this.props.t("common:sidenav.pricing.partnerPrices.menu")}
                  </Sidenav.RouterLink>
                </>
              )}
            </Sidenav.Menu>
          )}

          {/* Pricing Manager (not an admin): driver and supplier prices in the Price Editor only */}
          {!hasRole(selectedWebsite, "Administrator") && hasRole(selectedWebsite, PricingManagerRole) && (
            <Sidenav.RouterLink to="/price-editor" exact={true} icon="ion ion-ios-grid">
              Price Editor
            </Sidenav.RouterLink>
          )}

          {hasRole(selectedWebsite, "Driver") && !isAirportGreeter && (
            <Sidenav.Menu
              icon="ion ion-ios-pricetag"
              linkText={this.props.t("common:sidenav.incidents.menu")}
              active={this.isMenuActive(["/incidents/my"])}
              open={this.isMenuOpen(["/incidents/my"])}
            >
              <Sidenav.RouterLink to="/incidents/my" exact={true} icon="ion ion-ios-alert">
                {this.props.t("common:sidenav.incidents.complaintDirectly.menu")}
              </Sidenav.RouterLink>
            </Sidenav.Menu>

          )}
          {hasRole(selectedWebsite, "Driver") && !isAirportGreeter && (
            <Sidenav.RouterLink to="/payments-driver" exact={true} icon="ion ion-ios-wallet">
              {this.props.t("common:sidenav.payments.menu")}
            </Sidenav.RouterLink>
          )}
          {hasRole(selectedWebsite, "Client") && (
            <Sidenav.RouterLink to="/payments-client" exact={true} icon="ion ion-ios-wallet">
              {this.props.t("common:sidenav.payments.menu")}
            </Sidenav.RouterLink>
          )}
          {hasRole(selectedWebsite, "Administrator") && (
            <Sidenav.Menu
              icon="ion ion-ios-pricetag"
              linkText={this.props.t("common:sidenav.incidents.menu")}
              active={this.isMenuActive([
                "/incidents/complaint-directly",
                "/incidents/complaint-ticket",
                "/incidents/penalty-rules",
                "/incidents/policies",
                "/incidents/my",
                isTravelThruWebsite ? "/incidents/complaint-auto" : null,
                canShowReviews && isTravelThruWebsite ? "/reviews" : null,
              ])}
              open={this.isMenuOpen([
                "/incidents/complaint-directly",
                "/incidents/complaint-ticket",
                "/incidents/complaint-auto",
                "/incidents/penalty-rules",
                "/incidents/policies",
                "/incidents/my",
                canShowReviews && isTravelThruWebsite ? "/reviews" : null,
              ])}
            >
              <Sidenav.RouterLink to="/incidents/complaint-directly" exact={true} icon="ion ion-ios-alert">
                {isTravelThruWebsite ? this.props.t("common:sidenav.incidents.complaintDirectly.menuCustom") : this.props.t("common:sidenav.incidents.complaintDirectly.menu")}
              </Sidenav.RouterLink>
              {isTravelThruWebsite && (
                <>
                  {/* <Sidenav.RouterLink to="/incidents/complaint-ticket" exact={true} icon="ion ion-ios-alert">
                    {this.props.t("common:sidenav.incidents.complaintTicket.menu")}
                  </Sidenav.RouterLink> */}

                  <Sidenav.RouterLink to="/incidents/complaint-auto" exact={true} icon="ion ion-ios-alert">
                    {this.props.t("common:sidenav.incidents.complaintAuto.menu")}
                  </Sidenav.RouterLink>
                  {canShowReviews && (
                    <Sidenav.RouterLink to="/reviews" exact={true} icon="ion ion-ios-star">
                      {this.props.t("common:sidenav.reports.reviews.menu")}
                    </Sidenav.RouterLink>
                  )}
                </>
              )}
              {/* <Sidenav.RouterLink
                to="/incidents/complaint-ticket"
                exact={true}
                icon="ion ion-ios-warning"
              >
                {this.props.t("common:sidenav.incidents.complaintTicket.menu")}
              </Sidenav.RouterLink> */}
              {isTravelThru && (
                <>
                  {" "}
                  <Sidenav.RouterLink to="/incidents/penalty-rules" exact={true} icon="ion ion-ios-document">
                    {this.props.t("common:sidenav.incidents.penaltyRules.menu")}
                  </Sidenav.RouterLink>
                  <Sidenav.RouterLink to="/incidents/policies" exact={true} icon="ion ion-ios-paper">
                    {this.props.t("common:sidenav.incidents.policies.menu")}
                  </Sidenav.RouterLink>
                </>
              )}
            </Sidenav.Menu>
          )}

          {this.props.isSuperAdmin && (
            <Sidenav.Menu
              icon="ion ion-ios-settings"
              linkText={this.props.t("common:sidenav.admin.menu")}
              active={this.isMenuActive(["/admin-settings", "/admin/websites-management", "/admin/users", "/admin/node-diagnostics"])}
              open={this.isMenuOpen(["/admin-settings", "/admin/websites-management", "/admin/users", "/admin/node-diagnostics"])}
            >
              <Sidenav.RouterLink to="/admin-settings" exact={true} icon="ion ion-ios-cog">
                {this.props.t("common:sidenav.cache.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to={`/admin/websites-management`} exact={true} icon="ion ion-ios-settings">
                {this.props.t("common:sidenav.websites.menu")}
              </Sidenav.RouterLink>
              <Sidenav.RouterLink to={`/admin/node-diagnostics`} exact={true} icon="ion ion-ios-pulse">
                {this.props.t("common:sidenav.diagnostics.menu")}
              </Sidenav.RouterLink>
            </Sidenav.Menu>
          )}

          {((this.state.enableChat || this.state.enablePhoneSupport) || hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher", "Agent", "Support Operator"])) && hasAnyRole(selectedWebsite, ["Administrator", "Dispatcher", "Agent", "Support Operator"]) && (
            <Sidenav.Menu
              icon="ion ion-ios-chatbubbles"
              linkText={this.props.t("common:sidenav.support.menu")}
              active={this.isMenuActive(["/support/conversations", "/support/calls", "/support/sms", "/support/notifications"])}
              open={this.isMenuOpen(["/support/conversations", "/support/calls", "/support/sms", "/support/notifications"])}
            >
              {this.state.enableChat && (
                <Sidenav.RouterLink to="/support/conversations" exact={true} icon="ion ion-ios-chatboxes">
                  {this.props.t("common:sidenav.support.conversations.menu")}
                </Sidenav.RouterLink>
              )}
              {this.state.enablePhoneSupport && (
                <Sidenav.RouterLink to="/support/calls" exact={true} icon="ion ion-ios-call">
                  {this.props.t("common:sidenav.support.calls.menu")}
                </Sidenav.RouterLink>
              )}
              {/* No flag of its own — SMS follows the menu it lives in. Administrator only: the SMS log
                  exposes message content, numbers and cost; the backend endpoint enforces the same rule. */}
              {(this.props.isSuperAdmin || hasAnyRole(selectedWebsite, ["Administrator"])) && (
                <Sidenav.RouterLink to="/support/sms" exact={true} icon="ion ion-ios-text">
                  {this.props.t("common:sidenav.support.sms.menu")}
                </Sidenav.RouterLink>
              )}
              <Sidenav.RouterLink to="/support/notifications" exact={true} icon="ion ion-ios-notifications-outline">
                {this.props.t("common:sidenav.support.notifications.menu", "Notifications")}
              </Sidenav.RouterLink>
            </Sidenav.Menu>
          )}

          {!this.props.isSuperAdmin && hasAnyRole(selectedWebsite, ["Administrator"]) && (
            <Sidenav.RouterLink to={`/websites/${selectedWebsite.websiteGuid}`} exact={true} icon="ion ion-ios-settings">
              {this.props.t("common:sidenav.websiteConfig.menu")}
            </Sidenav.RouterLink>
          )}
        </div>
      </Sidenav>
    );
  }
}

LayoutSidenav.propTypes = {
  orientation: PropTypes.oneOf(["vertical", "horizontal"]),
};

LayoutSidenav.defaultProps = {
  orientation: "vertical",
};

export default withTranslation()(
  connect((store) => ({
    sidenavBg: store.theme.sidenavBg,
    websites: store.user.websites,
    isBookingDotCom: store.user.isBookingDotCom,
    isSuperAdmin: store.user.isSuperAdmin,
  }))(LayoutSidenav)
);
